import { EventStreamContentType, fetchEventSource } from '@microsoft/fetch-event-source';
import type { SseEnvelope } from '@/types/notifications';

// SSE-клиент уведомлений. Нативный EventSource не умеет слать Authorization, поэтому используем
// fetch-event-source. Свой reconnect-loop (а не внутренний ретрай библиотеки) — чтобы на каждом
// переподключении брать СВЕЖИЙ токен из tokenStore и корректно обрабатывать 401 → refresh.

interface SseClientOptions {
  readonly url: string;
  readonly getToken: () => string | undefined;
  readonly onMessage: (msg: SseEnvelope) => void;
  /** Refresh при 401 в потоке. Вернуть новый токен, либо undefined — сессия мертва (стоп). */
  readonly onAuthError: () => Promise<string | undefined>;
}

class FatalSseError extends Error {}
class AuthSseError extends Error {}

const BASE_BACKOFF_MS = 1_000;
const MAX_BACKOFF_MS = 30_000;
// Лимит подряд идущих 401 БЕЗ успешного коннекта между ними: защищает от tight-loop, если refresh
// выдаёт токен, но SSE всё равно отвергает его (мисконфиг). Сбрасывается при успешном onopen.
const MAX_AUTH_ATTEMPTS = 3;

export class SseClient {
  private readonly options: SseClientOptions;
  private controller: AbortController | null = null;
  private active = false;
  private attempt = 0;
  private authAttempts = 0;

  constructor(options: SseClientOptions) {
    this.options = options;
  }

  start(): void {
    if (this.active) {
      return;
    }
    this.active = true;
    this.attempt = 0;
    this.authAttempts = 0;
    void this.loop();
  }

  stop(): void {
    this.active = false;
    this.controller?.abort();
    this.controller = null;
  }

  private async loop(): Promise<void> {
    while (this.active) {
      const controller = new AbortController();
      this.controller = controller;
      try {
        await this.connectOnce(controller.signal);
      } catch (error) {
        if (!this.active) {
          return;
        }
        if (error instanceof FatalSseError) {
          this.active = false;
          return;
        }
        if (error instanceof AuthSseError) {
          this.authAttempts += 1;
          if (this.authAttempts > MAX_AUTH_ATTEMPTS) {
            // Сдаёмся: refresh даёт токен, но SSE его не принимает. Колокольчик продолжает жить на
            // REST; SSE поднимется при следующем заходе в кабинет. Не крутим бесконечный 401-loop.
            this.active = false;
            return;
          }
          const token = await this.options.onAuthError();
          if (!token || !this.active) {
            // Сессия мертва — refreshManager уже эмитнул onSessionExpired (logout). Останавливаемся.
            this.active = false;
            return;
          }
          await this.backoff(controller.signal); // backoff даже при «успешном» refresh — не tight-loop
          continue;
        }
        // Транзиентная ошибка (сеть/закрытие/5xx/429) — переподключаемся с backoff ниже.
      }
      if (!this.active) {
        return;
      }
      await this.backoff(controller.signal);
    }
  }

  private async connectOnce(signal: AbortSignal): Promise<void> {
    const token = this.options.getToken();
    await fetchEventSource(this.options.url, {
      signal,
      openWhenHidden: true, // держим поток в фоновой вкладке — иначе теряем события до возврата
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      onopen: async (response) => {
        const contentType = response.headers.get('content-type') ?? '';
        if (response.ok && contentType.includes(EventStreamContentType)) {
          this.attempt = 0; // успешный коннект — сброс backoff
          this.authAttempts = 0; // и счётчика auth-попыток
          return;
        }
        // 401 (нет/протух токен) и 403 (часть шлюзов отдаёт его на протухший/невалидный JWT) →
        // пробуем refresh и переподключаемся. Если это настоящий отказ по роли — refresh не поможет,
        // и после MAX_AUTH_ATTEMPTS сдаёмся (как и раньше при фатале). Бесконечного цикла нет.
        if (response.status === 401 || response.status === 403) {
          throw new AuthSseError();
        }
        if (response.status >= 400 && response.status < 500 && response.status !== 429) {
          throw new FatalSseError(`SSE open failed: ${response.status}`);
        }
        throw new Error(`SSE open failed: ${response.status}`); // транзиентная
      },
      onmessage: (event) => {
        if (!event.data) {
          return;
        }
        try {
          this.options.onMessage(JSON.parse(event.data) as SseEnvelope);
        } catch {
          // Повреждённое сообщение не должно рвать поток — пропускаем.
        }
      },
      onclose: () => {
        // Сервер закрыл соединение (in-memory эмиттеры, TTL/рестарт) → переподключаемся.
        throw new Error('SSE closed');
      },
      onerror: (error) => {
        // Пробрасываем наружу (в наш loop), а не отдаём библиотеке на внутренний ретрай —
        // так гарантируем свежий токен на следующем коннекте. Бросок прерывает fetchEventSource.
        throw error;
      },
    });
  }

  private async backoff(signal: AbortSignal): Promise<void> {
    this.attempt += 1;
    const capped = Math.min(BASE_BACKOFF_MS * 2 ** (this.attempt - 1), MAX_BACKOFF_MS);
    const ms = capped * 0.7 + capped * 0.3 * Math.random(); // джиттер 70–100%
    await delay(ms, signal);
  }
}

/** Пауза, прерываемая по abort (stop()) — чтобы остановка не висла до конца backoff. */
function delay(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }
    const id = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    function onAbort(): void {
      clearTimeout(id);
      resolve();
    }
    signal.addEventListener('abort', onAbort, { once: true });
  });
}
